import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tournamentsService } from '../../services/supabaseService';
import Header from '../../components/navigation/Header';
import Breadcrumb from '../../components/navigation/Breadcrumb';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const DeleteConfirmModal = ({ tournament, onConfirm, onCancel, deleting }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div className="relative bg-card rounded-xl shadow-2xl border border-border w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <Icon name="Trash2" className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">Delete Tournament</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Are you sure you want to delete <span className="font-semibold text-foreground">{tournament?.name}</span>?
        </p>
        <p className="text-sm text-red-600 dark:text-red-400 mb-6">
          This will permanently delete the tournament and all related data including predictions, participants, matches, and squads. This action cannot be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={onConfirm}
            disabled={deleting}
            iconName={deleting ? undefined : 'Trash2'}
            iconPosition="left"
            className="bg-red-600 hover:bg-red-700 text-white border-red-600"
          >
            {deleting ? 'Deleting...' : 'Delete Tournament'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const TournamentsList = () => {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [filteredTournaments, setFilteredTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteModal, setDeleteModal] = useState(null); // tournament to delete
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    fetchTournaments();
  }, []);

  useEffect(() => {
    filterTournaments();
  }, [searchQuery, statusFilter, tournaments]);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const data = await tournamentsService?.getAll();
      setTournaments(data || []);
    } catch (err) {
      console.error('Error fetching tournaments:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterTournaments = () => {
    let filtered = tournaments;

    if (searchQuery) {
      filtered = filtered?.filter(t => 
        t?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        t?.format?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered?.filter(t => t?.status === statusFilter);
    }

    setFilteredTournaments(filtered);
  };

  const handleDeleteClick = (tournament) => {
    setDeleteError('');
    setDeleteModal(tournament);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal) return;
    try {
      setDeleting(true);
      setDeleteError('');
      await tournamentsService?.delete(deleteModal?.id);
      setTournaments(prev => prev?.filter(t => t?.id !== deleteModal?.id));
      setDeleteModal(null);
    } catch (err) {
      console.error('Error deleting tournament:', err);
      setDeleteError('Failed to delete tournament. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (!deleting) {
      setDeleteModal(null);
      setDeleteError('');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'upcoming':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'completed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading tournaments...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumb />
      {deleteModal && (
        <DeleteConfirmModal
          tournament={deleteModal}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          deleting={deleting}
        />
      )}
      <main className="main-content">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 md:mb-8">
            <div className="mb-4 md:mb-0">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                All Tournaments
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Manage and view all PowerPlay tournaments
              </p>
            </div>
            <Button
              onClick={() => navigate('/tournament-management/new')}
              iconName="PlusCircle"
              iconPosition="left"
              className="w-full md:w-auto"
            >
              Create Tournament
            </Button>
          </div>

          {deleteError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
              {deleteError}
            </div>
          )}

          {/* Filters */}
          <div className="bg-card rounded-lg shadow-elevation-sm border border-border p-4 md:p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Search Tournaments
                </label>
                <div className="relative">
                  <Icon
                    name="Search"
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5"
                  />
                  <Input
                    type="text"
                    placeholder="Search by name or format..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Filter by Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e?.target?.value)}
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Status</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-4">
            <p className="text-sm text-muted-foreground">
              Showing {filteredTournaments?.length} of {tournaments?.length} tournaments
            </p>
          </div>

          {/* Tournaments List */}
          {filteredTournaments?.length === 0 ? (
            <div className="bg-card rounded-lg shadow-elevation-sm border border-border p-12 text-center">
              <Icon name="Trophy" className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                No tournaments found
              </h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery || statusFilter !== 'all' ?'Try adjusting your filters' :'Create your first tournament to get started'}
              </p>
              {!searchQuery && statusFilter === 'all' && (
                <Button
                  onClick={() => navigate('/tournament-management/new')}
                  iconName="PlusCircle"
                  iconPosition="left"
                >
                  Create Tournament
                </Button>
              )}
            </div>
          ) : (
            <div className="bg-card rounded-lg shadow-elevation-sm border border-border overflow-hidden">
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                        Tournament
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                        Format
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                        Start Date
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                        Participants
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-foreground uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredTournaments?.map((tournament) => (
                      <tr key={tournament?.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <img
                                src={tournament?.bannerImage || 'https://images.unsplash.com/photo-1709307060138-97cc2ee11b99'}
                                alt={tournament?.bannerImageAlt || 'Tournament banner'}
                                className="h-10 w-10 rounded-lg object-cover"
                              />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-semibold text-foreground">
                                {tournament?.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-foreground">
                            {tournament?.format}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusBadgeClass(tournament?.status)}`}>
                            {tournament?.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground">
                          {formatDate(tournament?.startDate)}
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground">
                          {tournament?.tournamentParticipants?.[0]?.count || 0}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/tournament-details/${tournament?.id}`)}
                              iconName="Eye"
                              iconPosition="left"
                            >
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/tournament-management/${tournament?.id}`)}
                              iconName="Edit"
                              iconPosition="left"
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(tournament)}
                              iconName="Trash2"
                              iconPosition="left"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-border">
                {filteredTournaments?.map((tournament) => (
                  <div key={tournament?.id} className="p-4">
                    <div className="flex items-start gap-4 mb-3">
                      <img
                        src={tournament?.bannerImage || 'https://images.unsplash.com/photo-1709307060138-97cc2ee11b99'}
                        alt={tournament?.bannerImageAlt || 'Tournament banner'}
                        className="h-16 w-16 rounded-lg object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-foreground mb-1 truncate">
                          {tournament?.name}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          {tournament?.format}
                        </p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusBadgeClass(tournament?.status)}`}>
                          {tournament?.status}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                      <div>
                        <span className="text-muted-foreground">Start Date:</span>
                        <p className="text-foreground font-medium">{formatDate(tournament?.startDate)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Participants:</span>
                        <p className="text-foreground font-medium">{tournament?.tournamentParticipants?.[0]?.count || 0}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/tournament-details/${tournament?.id}`)}
                        iconName="Eye"
                        iconPosition="left"
                        className="flex-1"
                      >
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/tournament-management/${tournament?.id}`)}
                        iconName="Edit"
                        iconPosition="left"
                        className="flex-1"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(tournament)}
                        iconName="Trash2"
                        iconPosition="left"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TournamentsList;